import type ClientItem from 'owa-mail-store/lib/store/schema/ClientItem';
import { getStore as getMailStore } from 'owa-mail-store/lib/store/Store';
import getLastestNonDraftItemId from 'owa-mail-reading-pane-store-conversation/lib/utils/getLastestNonDraftItemId';
import { isReadingPaneStackedView } from 'owa-mail-organization-settings/lib/utils/readingPaneStackedViewUtils';
import { getListViewState, getStore as getListViewStore } from 'owa-mail-list-store';
import { openChatPane, type OpenChatPaneOptions } from 'owa-copilot-dab/lib/utils/openChatPane';
import { openAgentChatPane } from 'owa-copilot-dab/lib/utils/openAgentChatPane';
import {
    MessageAnnotationSource,
    QueryAnnotationType,
    type QueryAnnotation,
} from '@1js/sydney-types';
import getConversationReadingPaneViewState from 'owa-mail-reading-pane-store-conversation/lib/utils/getConversationReadingPaneViewState';
import getItemReadingPaneViewState from 'owa-mail-reading-pane-store/lib/utils/getItemReadingPaneViewState';
import type { OpenBizChatSidePaneParams } from 'owa-bizchat-types';
import { isGlobalFeatureEnabled } from 'owa-feature-flags';
import type { MailboxInfo } from 'owa-client-types';
import { getMailboxInformationForAnnotation } from 'owa-bizchat-account-utils';
import { getPendingContext } from 'owa-bizchat-shared/lib/utils/PostMessageToFrameHandler';
import type { AgentType } from 'owa-bizchat-shared';
import isPopout from 'owa-popout-v2/lib/utils/isPopout';
import { isNonExecutableCmmId } from './isNonExecutableCmmId';
import { mapContentItemsToQueryAnnotations } from './mapContentItemsToQueryAnnotations';

/**
 * Specific variation of `executePrompt` that grabs the current selected mail's queryAnnotation to send
 * to Copilot Chat along with the rest of the prompt parameters.
 */
export const executePromptWithQueryAnnotation = (
    promptText: OpenChatPaneOptions['promptText'],
    /** Also known as "CMM ID" */
    formCode: OpenBizChatSidePaneParams['formCode'],
    targetWindow: Window,
    mailboxInfo: MailboxInfo,
    queryAnnotations?: QueryAnnotation[],
    agentType?: AgentType
): void => {
    const resolvedQueryAnnotations =
        queryAnnotations ?? getQueryAnnotations(targetWindow, mailboxInfo);
    const shouldExecuteQuery = !isNonExecutableCmmId(formCode);

    if (agentType) {
        openAgentChatPane({
            promptText,
            targetWindow,
            agentType,
            shouldExecuteQuery,
            formCode,
            queryAnnotations: resolvedQueryAnnotations,
        });
        return;
    }

    openChatPane({
        params: {
            formCode,
            queryAnnotations: resolvedQueryAnnotations,
            shouldExecuteQuery,
        },
        promptText,
        targetWindow,
    });
};

const getQueryAnnotations = (targetWindow: Window, mailboxInfo: MailboxInfo): QueryAnnotation[] => {
    // For popout windows, use getPendingContext (store-based path doesn't work in popouts)
    if (isPopout(targetWindow)) {
        const contextualChat = getPendingContext(mailboxInfo, targetWindow);
        if (contextualChat?.contentItems) {
            return mapContentItemsToQueryAnnotations(contextualChat.contentItems);
        }
        return [];
    }

    // Main window: existing store-based logic
    const { selectedTableViewId } = getListViewState();
    const tableView = getListViewStore().tableViews.get(selectedTableViewId);
    const isConversationViewRP = isReadingPaneStackedView(tableView?.tableQuery.listViewType);

    const conversationReadingPaneViewState = getConversationReadingPaneViewState();
    const itemReadingPaneViewState = getItemReadingPaneViewState();

    const id = isConversationViewRP
        ? conversationReadingPaneViewState?.conversationId?.Id
        : itemReadingPaneViewState?.itemId;

    const clientItem = getClientItem(id, isConversationViewRP);
    const mailboxInformation = getMailboxInformationForAnnotation(mailboxInfo);
    const queryAnnotationType =
        isGlobalFeatureEnabled('rp-summary-for-conversations') && isConversationViewRP
            ? QueryAnnotationType.EmailConversation
            : QueryAnnotationType.Message;

    return [
        {
            id: isGlobalFeatureEnabled('rp-summary-for-conversations')
                ? id
                : clientItem?.ItemId?.Id || '',
            messageAnnotationSource: MessageAnnotationSource.ClientAnnotated,
            queryAnnotationType,
            text: clientItem?.Subject || '',
            messageAnnotationMetadata: {
                '@type': queryAnnotationType,
                mailboxInformation,
            },
        },
    ];
};

const getClientItem = (id: string, isConversationViewRP: boolean): ClientItem | undefined => {
    const { items, conversations } = getMailStore();
    const conversationId = conversations.get(id)?.conversationId.Id;
    const useLatestItemId = isConversationViewRP && conversationId;

    return items.get(useLatestItemId ? getLastestNonDraftItemId(conversationId) : id);
};
